import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { KeyRound, LogOut, Settings, User } from "lucide-react";
import { useTranslation } from "react-i18next";

interface UserMenuProps {
  userName?: string;
  userRole?: string;
  onProfileClick: () => void;
  onChangePasswordClick: () => void;
  onLogout: () => void;
  collapsed?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({
  userName,
  userRole,
  onProfileClick,
  onChangePasswordClick,
  onLogout,
  collapsed = false,
}) => {
  const { t } = useTranslation("nav");

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={`flex items-center rounded-lg cursor-pointer transition-all duration-200 active:scale-95 ${
          collapsed
            ? "w-full p-2 justify-center hover:bg-bg-elevated"
            : "p-1.5 gap-2 hover:bg-bg-elevated"
        }`}
      >
        <div
          className={`bg-gradient-to-br from-brand-200 to-brand-300 rounded-full flex items-center justify-center flex-shrink-0 ${collapsed ? "w-9 h-9" : "w-7 h-7"}`}
        >
          <User
            className={collapsed ? "w-5 h-5 text-white" : "w-4 h-4 text-white"}
          />
        </div>
        {!collapsed && (
          <div className="min-w-0 text-left">
            <div className="text-xs font-medium text-text-primary truncate max-w-[90px]">
              {userName || t("user")}
            </div>
            <div className="text-[10px] text-text-muted leading-tight">
              {userRole === "admin" ? t("admin") : t("user")}
            </div>
          </div>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        className="w-48 origin-bottom bg-bg-surface rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1 z-50 border border-border-subtle animate-scale-in"
        sideOffset={5}
        side="top"
        align="start"
      >
        <DropdownMenu.Item
          className="group flex w-full items-center gap-3 px-4 py-2 text-sm text-text-primary cursor-pointer hover:bg-bg-elevated outline-none"
          onSelect={onProfileClick}
        >
          <Settings className="w-4 h-4 text-text-muted" />
          {t("settings")}
        </DropdownMenu.Item>
        <DropdownMenu.Item
          className="group flex w-full items-center gap-3 px-4 py-2 text-sm text-text-primary cursor-pointer hover:bg-bg-elevated outline-none"
          onSelect={onChangePasswordClick}
        >
          <KeyRound className="w-4 h-4 text-text-muted" />
          {t("changePassword")}
        </DropdownMenu.Item>
        <div className="border-t border-border-subtle my-1" />
        <DropdownMenu.Item
          className="group flex w-full items-center gap-3 px-4 py-2 text-sm text-danger cursor-pointer hover:bg-bg-elevated outline-none"
          onSelect={onLogout}
        >
          <LogOut className="w-4 h-4" />
          {t("logout")}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default UserMenu;
